import type { CurrentUserOrganization } from "@multi-chat/shared";

/**
 * Deterministic selected-organization reconciliation.
 * While memberships are still loading, keep the persisted id.
 * Never replace a still-active membership with "first organization".
 */
export function reconcileSelectedOrganizationId(input: {
  membershipsReady: boolean;
  organizationIds: readonly number[];
  storedId: number | null;
  /** Organization just created. Must not be replaced by first-membership fallback. */
  pendingCreatedOrganizationId?: number | null;
}): { selectedId: number | null; persist: boolean; clearPending: boolean } {
  const pending = input.pendingCreatedOrganizationId ?? null;
  if (pending !== null) {
    const arrived =
      input.membershipsReady && input.organizationIds.includes(pending);
    return {
      selectedId: pending,
      persist: arrived,
      clearPending: arrived,
    };
  }
  if (!input.membershipsReady) {
    return { selectedId: input.storedId, persist: false, clearPending: false };
  }
  if (input.organizationIds.length === 0) {
    return { selectedId: null, persist: input.storedId !== null, clearPending: false };
  }
  if (input.storedId !== null && input.organizationIds.includes(input.storedId)) {
    return { selectedId: input.storedId, persist: false, clearPending: false };
  }
  const fallback = input.organizationIds[0] ?? null;
  return {
    selectedId: fallback,
    persist: fallback !== input.storedId,
    clearPending: false,
  };
}

/**
 * Fresh authentication opens the chooser unless the person owns exactly one
 * organization and has no pending invitations. A Platform Admin always gets the
 * chooser, which also offers the Super Admin workspace.
 * A normal refresh stays in the still-accessible selected organization.
 */
export function shouldEnterWorkspaceDirectly(input: {
  organizations: readonly Pick<CurrentUserOrganization, "id" | "role" | "membershipRole">[];
  pendingInvitationCount?: number;
  storedId: number | null;
  freshAuthentication: boolean;
  workspaceEntered: boolean;
  platformAdmin?: boolean;
}): boolean {
  const organizationIds = input.organizations.map((organization) => organization.id);
  const pendingInvitationCount = input.pendingInvitationCount ?? 0;

  if (!input.freshAuthentication) {
    if (organizationIds.length === 0) return false;
    const storedIsActive =
      input.storedId !== null && organizationIds.includes(input.storedId);
    if (input.workspaceEntered && storedIsActive) return true;
    if (storedIsActive) return true;
    return organizationIds.length === 1;
  }

  if (input.platformAdmin) return false;
  if (pendingInvitationCount > 0) return false;
  if (organizationIds.length !== 1) return false;
  const only = input.organizations[0];
  return only !== undefined && membershipRoleOf(only) === "OWNER";
}

/**
 * The caller's real OrganizationMember role, or null when the organization is
 * reachable only through Platform Admin access. `/me` reports `membershipRole`
 * on every own membership; organizations entered from the Platform Admin
 * directory have none.
 */
export function realMembershipRoleOf(
  organization: Pick<CurrentUserOrganization, "role"> &
    Partial<Pick<CurrentUserOrganization, "access" | "membershipRole">>,
): CurrentUserOrganization["role"] | null {
  if (organization.membershipRole) return organization.membershipRole;
  return organization.access === "PLATFORM_ADMIN" ? null : organization.role;
}

/**
 * Switcher groups. A real membership wins over Platform Admin access, so each
 * organization lands in exactly one group: owned, member, or system-managed.
 */
export function groupOrganizations<
  T extends Pick<CurrentUserOrganization, "id" | "role"> &
    Partial<Pick<CurrentUserOrganization, "access" | "membershipRole">>,
>(organizations: readonly T[]): { owned: T[]; members: T[]; systemManaged: T[] } {
  const owned: T[] = [];
  const members: T[] = [];
  const systemManaged: T[] = [];
  for (const organization of organizations) {
    const role = realMembershipRoleOf(organization);
    if (role === null) systemManaged.push(organization);
    else if (role === "OWNER") owned.push(organization);
    else members.push(organization);
  }
  return { owned, members, systemManaged };
}

/** Real role of one of the caller's own memberships, even when entered as Platform Admin. */
export function membershipRoleOf(
  organization: Pick<CurrentUserOrganization, "role" | "membershipRole">,
): CurrentUserOrganization["role"] {
  return organization.membershipRole ?? organization.role;
}

/**
 * Workspace chooser groups. The chooser lists only the caller's own memberships
 * (never Platform Admin managed organizations), grouped by real membership role.
 */
export function groupChooserMemberships<
  T extends Pick<CurrentUserOrganization, "id" | "role" | "membershipRole">,
>(memberships: readonly T[]): { owned: T | null; members: T[] } {
  const owned = memberships.find((organization) => membershipRoleOf(organization) === "OWNER") ?? null;
  const members = memberships.filter((organization) => organization !== owned);
  return { owned, members };
}
